import React from 'react';
import { Button } from '@eventbrite/marmalade';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { GET_TICKETS_MESSAGE } from '../CheckoutButton/CheckoutButton';
import styles from './ExternalTicketsPanel.module.scss';
import { getPanelDisplayPriceFromPriceInfo } from '../utils';
import { getDisplayTimezone, getSingleDateDisplayString } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import { MULTIPLE_DATES } from '@/components/_shared/constants';

export const ON_PROVIDER_MESSAGE = defineMessage({
  id: 'On {provider}',
  defaultMessage: 'On {provider}',
  description: 'External ticketing provider label in the conversion bar',
});

export const ExternalTicketsPanel: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const { externalTicketing, endDate, hideEndDate, startDate, hideStartDate, isSeries, isOnline } = useBasicInfo();
  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);
  const externalTicketsUrl = externalTicketing?.externalTicketsUrl ?? '';
  const externalTicketingProviderName = externalTicketing?.externalTicketingProviderName ?? '';
  const externalTicketsAreAvailable = externalTicketing?.externalTicketsAreAvailable ?? false;

  if (!externalTicketsAreAvailable || !externalTicketsUrl) {
    return null;
  }

  const providerText = externalTicketingProviderName
    ? formatMessage(ON_PROVIDER_MESSAGE, { provider: externalTicketingProviderName })
    : '';

  const priceText = getPanelDisplayPriceFromPriceInfo(
    {
      minimumTicketPrice: externalTicketing?.minimumTicketPrice,
      maximumTicketPrice: externalTicketing?.maximumTicketPrice,
      isFree: externalTicketing?.isFree,
    },
    locale,
    formatMessage,
  );

  const headlineText = [priceText, providerText].filter(Boolean).join(' · ');

  const showEndTimeOnly = hideStartDate && !hideEndDate && endDate?.utc;

  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: startDate?.timezone,
    attendeeTimezone,
  });

  const dateString = isSeries
    ? formatMessage(MULTIPLE_DATES)
    : startDate
      ? getSingleDateDisplayString(
          startDate,
          !hideStartDate,
          locale,
          timezone,
          formatMessage,
          false,
          !showEndTimeOnly ? ' • ' : undefined,
          true,
          isOnline ?? false,
        )
      : undefined;

  return (
    <div className={styles.container} data-testid="external-tickets-panel">
      <div className={styles.content}>
        {headlineText && (
          <div data-testid="conversion-bar-headline" className={styles.headline}>
            <span>{headlineText}</span>
          </div>
        )}
        {dateString && (
          <div data-testid="conversion-bar-date" className={styles.date}>
            {dateString}
          </div>
        )}
      </div>
      <div className={styles.ctaButtonWrapper}>
        <Button
          variant="primary"
          onClick={() => window.open(externalTicketsUrl, '_blank', 'noreferrer noopener')}
          data-testid="external-tickets-link"
        >
          <FormattedMessage {...GET_TICKETS_MESSAGE} />
        </Button>
      </div>
    </div>
  );
};
